import { useEffect, useId, useRef, useState } from "react"
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Cpu,
  Loader2,
  Play,
  RefreshCw,
  Star,
} from "lucide-react"

import {
  CONCEPT_STRATEGIES,
  FEEDBACK_REASONS,
} from "@/components/deals/concept/concept-data"
import { buildConceptContract } from "@/components/deals/concept/concept-contract"
import { ConceptDecisionPanel } from "@/components/deals/concept/ConceptDecisionPanel"
import { ConceptExecutionActionCard } from "@/components/deals/concept/ConceptExecutionActionCard"
import { ConceptFeedbackDialog } from "@/components/deals/concept/ConceptFeedbackDialog"
import { ConceptStrategyCard } from "@/components/deals/concept/ConceptStrategyCard"
import { AiCreditUsageLink } from "@/components/shared/AiCreditUsageLink"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  addDealConceptExecutionAction,
  createDealConceptFeedback,
  dismissDealConceptExecutionAction,
  generateDealConceptCommercialisationStrategies,
  generateDealConceptInvestmentThesis,
  generateDealConceptValueEnhancements,
  getDealConcept,
  getDealConceptGeneration,
  selectDealConceptStrategy,
} from "@/lib/api"
import { aiCreditLimitMessage } from "@/lib/ai-credit-errors"
import { looksPersistedDealId } from "@/lib/persisted-deal"
import { useSession } from "@/lib/session-context"

const CONCEPT_REFRESH_ERROR_MESSAGE =
  "Concept Engine could not refresh strategies right now. Please try again."
const INVESTMENT_THESIS_ERROR_MESSAGE =
  "Investment Thesis could not generate right now. Please try again."
const VALUE_ENHANCEMENT_ERROR_MESSAGE =
  "Execution Actions could not generate right now. Please try again."
const ACTIVE_GENERATION_STATUSES = new Set(["queued", "running"])

function safeGenerationError(error, fallback) {
  return aiCreditLimitMessage(error) ?? fallback
}

function strategyPersistedId(strategy) {
  return strategy?.persistedId ?? strategy?.persisted_id ?? strategy?.id
}

function strategyMatchesSelection(strategy, selectionId) {
  if (!strategy || !selectionId) return false

  return strategyPersistedId(strategy) === selectionId || strategy.id === selectionId
}

function feedbackByStrategy(feedback = []) {
  return Object.fromEntries(feedback.filter(Boolean).map((item) => [item.strategyPersistedId ?? item.strategyId, true]))
}

function generationActive(generation) {
  return generation && ACTIVE_GENERATION_STATUSES.has(generation.status)
}

function generationModeFor(type) {
  if (type === "commercialisation_strategies") return "strategy"
  return "concept"
}

function generationDelay(generation) {
  return generation?.pollAfterMs ?? 2_000
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

function advanceEngineProgress(setEngineProgress, nextProgress) {
  setEngineProgress((currentProgress) => Math.max(currentProgress, nextProgress))
}

function strategiesFromPayload(payload, fallbackStrategies = []) {
  const topLevelStrategies = Array.isArray(payload?.strategies) ? payload.strategies : []
  if (topLevelStrategies.length > 0) return topLevelStrategies

  const conceptEngineStrategies = Array.isArray(payload?.conceptEngine?.strategies) ? payload.conceptEngine.strategies : []
  if (conceptEngineStrategies.length > 0) return conceptEngineStrategies

  return fallbackStrategies
}

async function pollConceptGeneration({
  dealId,
  csrfToken,
  startedPayload,
  requestId,
  latestGenerationRequestRef,
  setEngineProgress,
}) {
  let generation = startedPayload.generation
  if (!generationActive(generation)) return startedPayload

  while (generationActive(generation)) {
    advanceEngineProgress(setEngineProgress, generation.progress ?? 50)
    await sleep(generationDelay(generation))
    if (latestGenerationRequestRef.current !== requestId) return null

    const payload = await getDealConceptGeneration(dealId, generation.id, { csrfToken })
    if (latestGenerationRequestRef.current !== requestId) return null

    generation = payload.generation
    if (generation?.status === "failed") {
      throw new Error(generation.errorMessage || "Concept Engine generation failed.")
    }
    if (generation?.status === "succeeded") return payload
  }

  return startedPayload
}

export function DealConceptTab({
  deal,
  selectedStrategy: persistedStrategy,
  conceptView = null,
  priorityActionsRevision = "",
  onConceptViewChange,
  onConceptSelected,
  onExecutionActionChanged,
  onRunAppraisal,
}) {
  const { currentUser, csrfToken } = useSession()
  const shouldPersist = Boolean(currentUser && looksPersistedDealId(deal.id))
  const [strategies, setStrategies] = useState(() => (shouldPersist ? [] : CONCEPT_STRATEGIES))
  const [latestRun, setLatestRun] = useState(null)
  const [conceptEngine, setConceptEngine] = useState(null)
  const [hasRunEngineManually, setHasRunEngineManually] = useState(false)
  const [localSelectedStrategy, setLocalSelectedStrategy] = useState(undefined)
  const [feedbackConceptId, setFeedbackConceptId] = useState(null)
  const [localActiveView, setLocalActiveView] = useState("thesis")
  const [selectingStrategyId, setSelectingStrategyId] = useState(null)
  const [isGeneratingEnhancements, setIsGeneratingEnhancements] = useState(false)
  const [hasGeneratedStrategiesManually, setHasGeneratedStrategiesManually] = useState(false)
  const latestSelectionRequestRef = useRef(0)
  const [hasOpenedStrategyView, setHasOpenedStrategyView] = useState(
    () => conceptView === "strategy" || conceptView === "enhancement",
  )
  const [selectedReason, setSelectedReason] = useState("")
  const [additionalFeedback, setAdditionalFeedback] = useState("")
  const [submittedFeedback, setSubmittedFeedback] = useState({})
  const [isGenerating, setIsGenerating] = useState(false)
  const [generationMode, setGenerationMode] = useState("concept")
  const [engineProgress, setEngineProgress] = useState(10)
  const [error, setError] = useState(null)
  const [pendingExecutionActionId, setPendingExecutionActionId] = useState(null)
  const [acknowledgedReviewKey, setAcknowledgedReviewKey] = useState(null)
  const latestGenerationRequestRef = useRef(0)
  const persistedStrategyId = conceptStrategyIdFromSelection(persistedStrategy, strategies)
  const hasPersistedThesis = Boolean(conceptEngine?.thesis)
  const hasRunEngine =
    hasRunEngineManually || hasPersistedThesis || Boolean(persistedStrategyId) || (shouldPersist && strategies.length > 0)
  const selectedStrategy = localSelectedStrategy === undefined ? persistedStrategyId : localSelectedStrategy

  const selectedStrategyData =
    strategies.find((strategy) => strategyMatchesSelection(strategy, selectedStrategy)) ?? null
  const conceptContract = buildConceptContract({
    deal,
    strategies,
    selectedStrategy,
    latestRun,
    conceptEngine,
  })
  const evidenceReviewKey = reviewStateKey(conceptContract.reviewState)
  const isReviewOutOfDate = conceptContract.reviewState.isOutOfDate
  const shouldShowReviewRequired =
    isReviewOutOfDate && acknowledgedReviewKey !== evidenceReviewKey
  const hasGeneratedValueEnhancements = conceptContract.hasGeneratedValueEnhancements
  const hasGeneratedStrategies = hasGeneratedStrategiesManually || conceptContract.strategies.length > 0
  const canShowStrategyView =
    hasGeneratedStrategies && (
      hasOpenedStrategyView ||
      shouldPersist ||
      conceptView === "strategy" ||
      conceptView === "enhancement" ||
      Boolean(selectedStrategy)
    )
  const canShowEnhancementView =
    Boolean(selectedStrategy) && !selectingStrategyId && hasGeneratedValueEnhancements
  const activeView = allowedConceptView(conceptView ?? localActiveView, {
    canShowStrategyView,
    canShowEnhancementView,
  })

  const updateConceptView = (nextView) => {
    const allowedView = allowedConceptView(nextView, {
      canShowStrategyView,
      canShowEnhancementView,
    })

    setLocalActiveView(allowedView)
    onConceptViewChange?.(allowedView)
  }

  const applyConceptPayload = (payload, options = {}) => {
    const nextStrategies = strategiesFromPayload(payload, options.fallbackStrategies ?? [])
    setStrategies(nextStrategies)
    if (nextStrategies.length > 0) setHasGeneratedStrategiesManually(true)
    setLatestRun(payload.latestRun ?? options.fallbackLatestRun ?? null)
    setConceptEngine(payload.conceptEngine ?? options.fallbackConceptEngine ?? null)
    setSubmittedFeedback(feedbackByStrategy(payload.feedback))
    setLocalSelectedStrategy(strategyPersistedId(payload.selectedStrategy) ?? options.fallbackSelectedStrategy ?? null)
  }

  const runExecutionActionCommand = async (action, command) => {
    if (!shouldPersist || pendingExecutionActionId) return

    setPendingExecutionActionId(action.id)
    setError(null)
    try {
      const payload = await command(deal.id, action.id, { csrfToken })
      applyConceptPayload(payload)
      await onExecutionActionChanged?.()
    } catch (commandError) {
      setError(commandError.message)
    } finally {
      setPendingExecutionActionId(null)
    }
  }

  const openStrategyView = () => {
    setHasOpenedStrategyView(true)
    setLocalActiveView("strategy")
    onConceptViewChange?.("strategy")
  }

  const openEnhancementView = async () => {
    if (!selectedStrategy || selectingStrategyId || isGeneratingEnhancements) return

    if (shouldPersist && !hasGeneratedValueEnhancements) {
      const strategy = selectedStrategyData ?? strategies.find((item) => strategyMatchesSelection(item, selectedStrategy))
      if (!strategy) return

      try {
        const requestId = latestGenerationRequestRef.current + 1
        latestGenerationRequestRef.current = requestId
        setError(null)
        setEngineProgress(10)
        setIsGeneratingEnhancements(true)
        const startedPayload = await generateDealConceptValueEnhancements(
          deal.id,
          strategyPersistedId(strategy),
          { csrfToken },
        )
        const payload = await pollConceptGeneration({
          dealId: deal.id,
          csrfToken,
          startedPayload,
          requestId,
          latestGenerationRequestRef,
          setEngineProgress,
        })
        if (!payload || latestGenerationRequestRef.current !== requestId) return

        applyConceptPayload(payload, {
          fallbackStrategies: strategies,
          fallbackLatestRun: latestRun,
          fallbackConceptEngine: conceptEngine,
          fallbackSelectedStrategy: selectedStrategy,
        })
        onConceptSelected?.({
          deal: payload.deal,
          strategy: payload.selectedStrategy ?? strategy,
        })
      } catch (generationError) {
        setError(safeGenerationError(generationError, VALUE_ENHANCEMENT_ERROR_MESSAGE))
        return
      } finally {
        setIsGeneratingEnhancements(false)
      }
    }

    setHasOpenedStrategyView(true)
    setLocalActiveView("enhancement")
    onConceptViewChange?.("enhancement")
  }

  useEffect(() => {
    if (!shouldPersist) return undefined

    let ignore = false

    getDealConcept(deal.id, { csrfToken })
      .then((payload) => {
        if (ignore) return

        const nextStrategies = strategiesFromPayload(payload)
        setStrategies((currentStrategies) => (
          nextStrategies.length > 0 ? nextStrategies : currentStrategies
        ))
        if (nextStrategies.length > 0) setHasGeneratedStrategiesManually(true)
        setLatestRun(payload.latestRun ?? null)
        setConceptEngine(payload.conceptEngine ?? null)
        setSubmittedFeedback(feedbackByStrategy(payload.feedback))
        setLocalSelectedStrategy(payload.selectedStrategy ? strategyPersistedId(payload.selectedStrategy) : null)
        if (generationActive(payload.activeGeneration)) {
          const requestId = latestGenerationRequestRef.current + 1
          latestGenerationRequestRef.current = requestId
          setGenerationMode(generationModeFor(payload.activeGeneration.generationType))
          setEngineProgress(payload.activeGeneration.progress ?? 50)
          setIsGenerating(payload.activeGeneration.generationType !== "value_enhancements")
          setIsGeneratingEnhancements(payload.activeGeneration.generationType === "value_enhancements")
          pollConceptGeneration({
            dealId: deal.id,
            csrfToken,
            startedPayload: { ...payload, generation: payload.activeGeneration },
            requestId,
            latestGenerationRequestRef,
            setEngineProgress,
          })
            .then((resolvedPayload) => {
              if (!resolvedPayload || ignore || latestGenerationRequestRef.current !== requestId) return

              applyConceptPayload(resolvedPayload, {
                fallbackStrategies: nextStrategies,
                fallbackLatestRun: payload.latestRun ?? null,
                fallbackConceptEngine: payload.conceptEngine ?? null,
              })
              if (payload.activeGeneration.generationType === "commercialisation_strategies") {
                setHasOpenedStrategyView(true)
                setLocalActiveView("strategy")
                onConceptViewChange?.("strategy")
              }
              if (payload.activeGeneration.generationType === "value_enhancements") {
                setHasOpenedStrategyView(true)
                setLocalActiveView("enhancement")
                onConceptViewChange?.("enhancement")
              }
            })
            .catch((pollError) => {
              if (!ignore) setError(pollError.message)
            })
            .finally(() => {
              if (ignore || latestGenerationRequestRef.current !== requestId) return

              setIsGenerating(false)
              setIsGeneratingEnhancements(false)
            })
        }
      })
      .catch((loadError) => {
        if (!ignore) setError(loadError.message)
      })

    return () => { ignore = true }
  }, [csrfToken, deal.id, onConceptViewChange, priorityActionsRevision, shouldPersist])

  useEffect(() => {
    if (!isGenerating && !isGeneratingEnhancements) return undefined

    const timers = [
      setTimeout(() => advanceEngineProgress(setEngineProgress, 30), 500),
      setTimeout(() => advanceEngineProgress(setEngineProgress, 50), 1500),
      setTimeout(() => advanceEngineProgress(setEngineProgress, 80), 2500),
    ]

    return () => {
      timers.forEach((timer) => clearTimeout(timer))
    }
  }, [isGenerating, isGeneratingEnhancements])

  const resetFeedbackDialog = () => {
    setFeedbackConceptId(null)
    setSelectedReason("")
    setAdditionalFeedback("")
  }

  const handleFeedbackOpen = (conceptId) => {
    setFeedbackConceptId(conceptId)
    setSelectedReason("")
    setAdditionalFeedback("")
  }

  const handleFeedbackSubmit = async () => {
    if (!feedbackConceptId || !selectedReason) return

    if (shouldPersist) {
      const strategy = strategies.find((item) => strategyMatchesSelection(item, feedbackConceptId))
      try {
        setError(null)
        const payload = await createDealConceptFeedback(deal.id, strategyPersistedId(strategy), {
          rating: "down",
          reason: selectedReason,
          body: additionalFeedback,
          activitySource: "manual",
        }, { csrfToken })
        setSubmittedFeedback(feedbackByStrategy(payload.feedback ?? [payload.createdFeedback]))
        setConceptEngine(payload.conceptEngine ?? conceptEngine)
      } catch (feedbackError) {
        setError(feedbackError.message)
        return
      }
    } else {
      setSubmittedFeedback((currentFeedback) => ({
        ...currentFeedback,
        [feedbackConceptId]: true,
      }))
    }
    resetFeedbackDialog()
  }

  const handleRunEngine = async () => {
    if (!shouldPersist) {
      setHasRunEngineManually(true)
      return
    }

    try {
      const requestId = latestGenerationRequestRef.current + 1
      latestGenerationRequestRef.current = requestId
      setError(null)
      setEngineProgress(10)
      setGenerationMode("concept")
      setIsGenerating(true)
      const startedPayload = await generateDealConceptInvestmentThesis(deal.id, {
        activitySource: "system",
      }, { csrfToken })
      const payload = await pollConceptGeneration({
        dealId: deal.id,
        csrfToken,
        startedPayload,
        requestId,
        latestGenerationRequestRef,
        setEngineProgress,
      })
      if (!payload || latestGenerationRequestRef.current !== requestId) return

      applyConceptPayload(payload)
      setHasRunEngineManually(true)
      setHasGeneratedStrategiesManually(false)
      setHasOpenedStrategyView(false)
      updateConceptView("thesis")
    } catch (generationError) {
      setError(safeGenerationError(generationError, INVESTMENT_THESIS_ERROR_MESSAGE))
    } finally {
      setIsGenerating(false)
    }
  }

  const handleGenerateStrategies = async () => {
    if (hasGeneratedStrategies) {
      openStrategyView()
      return
    }

    if (!shouldPersist) {
      setStrategies(CONCEPT_STRATEGIES)
      setHasGeneratedStrategiesManually(true)
      openStrategyView()
      return
    }

    try {
      const requestId = latestGenerationRequestRef.current + 1
      latestGenerationRequestRef.current = requestId
      setError(null)
      setEngineProgress(10)
      setGenerationMode("strategy")
      setIsGenerating(true)
      const startedPayload = await generateDealConceptCommercialisationStrategies(deal.id, {
        activitySource: "system",
      }, { csrfToken })
      const payload = await pollConceptGeneration({
        dealId: deal.id,
        csrfToken,
        startedPayload,
        requestId,
        latestGenerationRequestRef,
        setEngineProgress,
      })
      if (!payload || latestGenerationRequestRef.current !== requestId) return

      applyConceptPayload(payload, {
        fallbackStrategies: strategies,
        fallbackLatestRun: latestRun,
        fallbackConceptEngine: conceptEngine,
      })
      setHasRunEngineManually(true)
      setHasGeneratedStrategiesManually(true)
      openStrategyView()
    } catch (generationError) {
      setError(safeGenerationError(generationError, CONCEPT_REFRESH_ERROR_MESSAGE))
    } finally {
      setIsGenerating(false)
    }
  }

  const handleStrategySelect = async (strategyId) => {
    const strategy = strategies.find((item) => strategyMatchesSelection(item, strategyId))
    if (!strategy || selectingStrategyId) return

    if (shouldPersist) {
      const requestId = latestSelectionRequestRef.current + 1
      latestSelectionRequestRef.current = requestId
      setSelectingStrategyId(strategyId)
      try {
        setError(null)
        const payload = await selectDealConceptStrategy(
          deal.id,
          strategyPersistedId(strategy),
          { csrfToken },
        )
        if (latestSelectionRequestRef.current !== requestId) return

        const nextStrategies = strategiesFromPayload(payload, strategies)
        setStrategies(nextStrategies)
        if (nextStrategies.length > 0) setHasGeneratedStrategiesManually(true)
        setLatestRun(payload.latestRun ?? latestRun)
        setConceptEngine(payload.conceptEngine ?? conceptEngine)
        setSubmittedFeedback(feedbackByStrategy(payload.feedback))
        setLocalSelectedStrategy(strategyPersistedId(payload.selectedStrategy) ?? strategyId)
        onConceptSelected?.({
          deal: payload.deal,
          strategy: payload.selectedStrategy ?? strategy,
        })
      } catch (selectError) {
        if (latestSelectionRequestRef.current === requestId) setError(selectError.message)
      } finally {
        if (latestSelectionRequestRef.current === requestId) setSelectingStrategyId(null)
      }
      return
    }

    setLocalSelectedStrategy(strategyId)
  }

  const handleRunAppraisal = () => {
    const selected = strategies.find((strategy) => strategyMatchesSelection(strategy, selectedStrategy))
    if (selected) onRunAppraisal?.(selected)
  }

  if (isGenerating || isGeneratingEnhancements) {
    return (
      <ConceptEngineRunningView
        progress={engineProgress}
        variant={isGeneratingEnhancements ? "enhancement" : generationMode}
      />
    )
  }

  if (!hasRunEngine) {
    return (
      <div className="mx-auto w-full max-w-[1200px] pb-24">
        <ConceptEngineHeader
          status="STANDBY"
          description="Sentinel analyses all uploaded evidence to identify the dominant value creation mechanism before generating investment strategies."
        />

        {error ? (
          <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
            {error} <AiCreditUsageLink error={error} />
          </div>
        ) : null}
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-10 text-center text-card-foreground shadow-sm">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Cpu className="h-8 w-8 text-primary" />
            </div>

            <h2 className="mb-2 text-xl font-bold text-foreground">
              Concept Engine
            </h2>
            <p className="mb-6 text-sm font-medium leading-relaxed text-muted-foreground">
              Sentinel analyses all uploaded evidence to identify the dominant value creation mechanism before generating investment strategies.
            </p>
            <p className="mb-8 text-sm font-medium leading-relaxed text-muted-foreground">
              It answers: &quot;What is this asset trying to become?&quot; rather than &quot;What could I do with this property?&quot;
            </p>

            <Button
              type="button"
              size="lg"
              onClick={handleRunEngine}
              className="h-12 w-full rounded-lg font-bold shadow-sm"
            >
              Generate Investment Thesis
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="mx-auto w-full max-w-[1200px] pb-24">
        {error ? (
          <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
            {error} <AiCreditUsageLink error={error} />
          </div>
        ) : null}
        {shouldShowReviewRequired ? (
          <ConceptReviewRequired
            reviewState={conceptContract.reviewState}
            onReview={() => {
              setAcknowledgedReviewKey(evidenceReviewKey)
              updateConceptView("thesis")
            }}
            onContinue={() => {
              setAcknowledgedReviewKey(evidenceReviewKey)
              updateConceptView("thesis")
            }}
          />
        ) : (
          <>
            <ConceptFlowNav
              activeView={activeView}
              canShowStrategyView={canShowStrategyView}
              canShowEnhancementView={canShowEnhancementView}
              onViewChange={updateConceptView}
            />

            <ConceptLiveHeader
              activeView={activeView}
              thesis={conceptContract.thesis}
              shouldPersist={shouldPersist}
              onRefresh={handleRunEngine}
            />

            {activeView === "thesis" ? (
              <InvestmentThesisView
                contract={conceptContract}
                isOutOfDate={isReviewOutOfDate}
                onGenerateStrategies={handleGenerateStrategies}
                onRegenerate={handleRunEngine}
                hasGeneratedStrategies={hasGeneratedStrategies}
              />
            ) : null}

            {activeView === "strategy" ? (
              <CommercialisationStrategyView
                strategies={conceptContract.strategies}
                selectedStrategy={selectedStrategy}
                selectedStrategyData={selectedStrategyData}
                submittedFeedback={submittedFeedback}
                selectingStrategyId={selectingStrategyId}
                isGeneratingEnhancements={isGeneratingEnhancements}
                isOutOfDate={isReviewOutOfDate}
                onSelect={handleStrategySelect}
                onFeedback={handleFeedbackOpen}
                onProceed={openEnhancementView}
                hasGeneratedValueEnhancements={hasGeneratedValueEnhancements}
              />
            ) : null}

            {activeView === "enhancement" ? (
              <ValueEnhancementView
                contract={conceptContract}
                isOutOfDate={isReviewOutOfDate}
                pendingActionId={pendingExecutionActionId}
                onBack={() => updateConceptView("strategy")}
                onAdd={(action) => runExecutionActionCommand(action, addDealConceptExecutionAction)}
                onDismiss={(action) => runExecutionActionCommand(action, dismissDealConceptExecutionAction)}
                onRunAppraisal={handleRunAppraisal}
              />
            ) : null}
          </>
        )}
      </div>

      <ConceptFeedbackDialog
        open={Boolean(feedbackConceptId)}
        reasons={FEEDBACK_REASONS}
        selectedReason={selectedReason}
        additionalFeedback={additionalFeedback}
        onOpenChange={(open) => {
          if (!open) resetFeedbackDialog()
        }}
        onReasonChange={setSelectedReason}
        onAdditionalFeedbackChange={setAdditionalFeedback}
        onSubmit={handleFeedbackSubmit}
        onCancel={resetFeedbackDialog}
      />
    </>
  )
}

function ConceptEngineHeader({ status, description }) {
  return (
    <section className="mb-6 rounded-lg border border-border bg-card p-5 text-card-foreground shadow-sm">
      <ConceptEngineHeaderContent status={status} description={description} />
    </section>
  )
}

function ConceptEngineHeaderContent({ status, description, isLive = false }) {
  return (
    <div>
      <h2 className="flex items-center gap-2 text-sm font-bold text-foreground">
        <Cpu className="h-4 w-4 text-primary" />
        <span>Concept Engine</span>
        <span
          className={
            isLive
              ? "ml-2 rounded bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary"
              : "ml-2 rounded bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
          }
        >
          {status}
        </span>
      </h2>
      <p className="mt-1 text-xs font-medium text-muted-foreground">
        {description}
      </p>
    </div>
  )
}

function ConceptLiveHeader({
  activeView,
  thesis,
  shouldPersist,
  onRefresh,
}) {
  const isStrategy = activeView === "strategy"
  const isEnhancement = activeView === "enhancement"
  const description = isStrategy
    ? 'Commercialisation Strategies answers: "How should we realise the value?"'
    : isEnhancement
      ? 'Execution Actions answers: "What should happen next?"'
      : "AI-generated value-add strategies based on deal data"

  return (
    <section className="mb-6 flex flex-col gap-4 rounded-lg border border-border bg-card p-5 text-card-foreground shadow-sm sm:flex-row sm:items-center sm:justify-between">
      <ConceptEngineHeaderContent
        status="LIVE"
        description={description}
        isLive
      />
      {isStrategy ? (
        <div className="shrink-0 border-border sm:border-l sm:pl-6 sm:text-right">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Generated From
          </span>
          <span className="inline-flex rounded bg-muted px-2.5 py-1 text-xs font-bold text-foreground">
            Thesis {formatShortEvidenceVersion(thesis.evidenceVersion)}
          </span>
        </div>
      ) : shouldPersist ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onRefresh}
          className="shrink-0 font-bold"
        >
          <RefreshCw aria-hidden="true" />
          Refresh Engine
        </Button>
      ) : null}
    </section>
  )
}

function ConceptEngineRunningView({ progress, variant = "concept" }) {
  const isEnhancement = variant === "enhancement"
  const isStrategy = variant === "strategy"
  const phase = conceptEnginePhase(progress, variant)
  const evidenceItems = isEnhancement
    ? ["Selected Strategy", "Investment Thesis", "Evidence Pack", "Commercial Constraints", "Risk Flags", "Vendor Notes"]
    : isStrategy
      ? ["Investment Thesis", "Evidence Pack", "Planning Constraints", "Income Potential", "Execution Complexity", "Risk Flags"]
    : ["Brochure", "Floorplans", "Market Data", "Planning", "Current Income", "Media", "Vendor Notes"]
  const stepLabels = isEnhancement
    ? ["Reviewing Selected Strategy", "Analysing Evidence and Constraints", "Creating Execution Actions"]
    : isStrategy
      ? ["Reviewing Investment Thesis", "Ranking Commercial Routes", "Calculating Optimal Strategies"]
    : ["Reviewing Evidence", "Diagnosing Opportunity", "Forming Investment Thesis"]

  return (
    <div
      role="status"
      aria-live="polite"
      className="mx-auto w-full max-w-[1200px] pb-24"
    >
      <div className="flex flex-col items-center justify-center py-24">
        <div className="w-full max-w-md">
          <h3 className="mb-6 text-center text-lg font-bold text-foreground">
            {phase}...
          </h3>

          <div className="mb-8 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div className={cn("h-full bg-primary transition-all duration-300", progressWidthClass(progress))} />
          </div>

          <div className="space-y-4">
            <RunningStep
              isActive={progress < 50}
              isComplete={progress >= 50}
              label={stepLabels[0]}
            />
            <div className={cn("mb-4 space-y-2 pl-8", progress < 30 && "hidden")}>
              {evidenceItems.map((item) => (
                <p key={item} className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                  {item}
                </p>
              ))}
            </div>
            <RunningStep
              isActive={progress >= 50 && progress < 80}
              isComplete={progress >= 80}
              label={stepLabels[1]}
            />
            <RunningStep
              isActive={progress >= 80}
              label={stepLabels[2]}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function RunningStep({ label, isActive = false, isComplete = false }) {
  return (
    <div className={isActive || isComplete ? "flex items-center gap-3" : "flex items-center gap-3 opacity-30"}>
      {isComplete ? (
        <CheckCircle2 className="h-5 w-5 text-primary" aria-hidden="true" />
      ) : isActive ? (
        <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
      ) : (
        <span className="h-5 w-5 rounded-full border-2 border-muted" aria-hidden="true" />
      )}
      <span className="text-sm font-medium text-foreground">{label}</span>
    </div>
  )
}

function conceptEnginePhase(progress, variant = "concept") {
  if (variant === "enhancement") {
    if (progress >= 80) return "Creating Execution Actions"
    if (progress >= 50) return "Analysing Evidence and Constraints"

    return "Reviewing Selected Strategy"
  }

  if (variant === "strategy") {
    if (progress >= 80) return "Calculating Optimal Strategies"
    if (progress >= 50) return "Ranking Commercial Routes"

    return "Reviewing Investment Thesis"
  }

  if (progress >= 80) return "Forming Investment Thesis"
  if (progress >= 50) return "Diagnosing Opportunity"

  return "Reviewing Evidence"
}

function progressWidthClass(progress) {
  if (progress >= 80) return "w-[80%]"
  if (progress >= 50) return "w-[50%]"
  if (progress >= 30) return "w-[30%]"

  return "w-[10%]"
}

const FLOW_STEPS = [
  { key: "thesis", label: "Investment Thesis" },
  { key: "strategy", label: "Strategy" },
  { key: "enhancement", label: "Execution Actions" },
]

function allowedConceptView(view, { canShowStrategyView, canShowEnhancementView }) {
  if (view === "enhancement") {
    if (canShowEnhancementView) return "enhancement"
    if (canShowStrategyView) return "strategy"

    return "thesis"
  }

  if (view === "strategy" && canShowStrategyView) return "strategy"

  return "thesis"
}

function reviewStateKey(reviewState) {
  return reviewState.changedEvidence.join("|") || "review-required"
}

function ConceptFlowNav({
  activeView,
  canShowStrategyView,
  canShowEnhancementView,
  onViewChange,
}) {
  return (
    <nav aria-label="Concept Engine workflow" className="mb-8 flex flex-wrap items-center justify-center gap-3">
      {FLOW_STEPS.map((step, index) => {
        const isLocked =
          (step.key === "strategy" && !canShowStrategyView) ||
          (step.key === "enhancement" && !canShowEnhancementView)
        const isActive = activeView === step.key

        return (
          <div key={step.key} className="flex items-center gap-3">
            {index > 0 ? (
              <ArrowRight className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
            ) : null}
            <button
              type="button"
              disabled={isLocked}
              onClick={() => onViewChange(step.key)}
              className={
                isActive
                  ? "text-xs font-bold uppercase tracking-widest text-foreground"
                  : "text-xs font-bold uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
              }
            >
              {step.label}
            </button>
          </div>
        )
      })}
    </nav>
  )
}

function ConceptReviewRequired({ reviewState, onReview, onContinue }) {
  return (
    <section className="mx-auto mb-8 flex max-w-[1200px] flex-col items-center justify-center py-12">
      <div className="w-full max-w-md rounded-2xl border border-chart-4/50 bg-card p-10 text-center text-card-foreground shadow-sm">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-chart-4/10">
          <AlertTriangle className="h-8 w-8 text-chart-4" aria-hidden="true" />
        </div>
        <h3 className="mb-4 text-xl font-bold text-foreground">Judgement Review Required</h3>
        <p className="mb-6 text-sm text-muted-foreground">
          Sentinel has detected new evidence which may materially affect your investment judgement.
        </p>
        {reviewState.changedEvidence.length > 0 ? (
          <div className="mb-6 rounded-xl border border-border bg-background p-4 text-left">
            <h4 className="mb-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Changes detected
            </h4>
            <ul className="space-y-2">
              {reviewState.changedEvidence.map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <CheckCircle2 className="h-4 w-4 text-chart-4" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <p className="mb-8 text-sm font-medium text-chart-4">
          Current recommendation may no longer represent the strongest strategy.
        </p>
        <div className="space-y-3">
          <Button
            type="button"
            disabled={!reviewState.reviewThesisAvailable}
            onClick={onReview}
            className="h-12 w-full rounded-xl bg-chart-4 font-bold text-primary-foreground shadow-md hover:bg-chart-4/90"
          >
            Review Investment Thesis
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={!reviewState.continuePreviousJudgementAvailable}
            onClick={onContinue}
            className="h-11 w-full rounded-xl font-bold"
          >
            Continue with previous judgement
          </Button>
        </div>
      </div>
    </section>
  )
}

function InvestmentThesisView({
  contract,
  isOutOfDate,
  onGenerateStrategies,
  onRegenerate,
  hasGeneratedStrategies,
}) {
  const thesis = contract.thesis
  const [showSupportingDetail, setShowSupportingDetail] = useState(false)
  const supportingDetailId = useId()

  return (
    <section className="mx-auto max-w-4xl rounded-lg border border-border bg-card p-8 text-card-foreground shadow-sm">
      {isOutOfDate ? (
        <OutdatedThesisBanner
          actionLabel="Regenerate Investment Thesis"
          onAction={onRegenerate}
        />
      ) : null}
      <div className="border-b border-border pb-8">
        <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Investment Thesis
        </h3>
        <p className="mt-4 text-xl font-semibold leading-relaxed text-foreground">
          {thesis.commercialJudgement}
        </p>
        <p className="mt-4 text-sm font-medium leading-relaxed text-muted-foreground">
          {thesis.thesisText}
        </p>
      </div>

      <Button
        type="button"
        variant="secondary"
        aria-expanded={showSupportingDetail}
        aria-controls={supportingDetailId}
        onClick={() => setShowSupportingDetail((current) => !current)}
        className="mt-6 w-full justify-between font-bold"
      >
        {showSupportingDetail ? "Hide Supporting Detail" : "View Supporting Detail"}
        <ChevronDown
          aria-hidden="true"
          className={cn("transition-transform", showSupportingDetail && "rotate-180")}
        />
      </Button>

      {showSupportingDetail ? (
        <div id={supportingDetailId} className="mt-6 border-t border-border pt-6">
          <dl className="grid gap-4 border-b border-border pb-6 sm:grid-cols-3">
            <MetaItem label="Generated From" value={formatEvidenceVersion(thesis.evidenceVersion)} />
            <MetaItem label="Generated" value={formatGeneratedTimestamp(thesis.generatedAt)} />
            <StatusMetaItem label="Status" value={isOutOfDate ? "Out of Date" : thesis.displayStatus} />
          </dl>

          <div className="mt-6">
            <div className="mb-2 flex items-center justify-between gap-4">
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Thesis Confidence
              </span>
              <span className="text-sm font-bold text-primary">
                {thesis.confidenceScore ?? "--"} / 100
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div className={cn("h-full bg-primary", confidenceWidthClass(thesis.confidenceScore))} />
            </div>
          </div>

          <section className="py-8">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Primary Value Drivers
            </h4>
            <ul className="mt-4 grid gap-4 md:grid-cols-2">
              {thesis.valueDrivers.map((driver) => (
                <ValueDriverCard key={driver.id} driver={driver} />
              ))}
            </ul>
          </section>

          <section>
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Opportunity Classification
            </h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {thesis.opportunityClassification.map((item) => (
                <span
                  key={item}
                  className="rounded-full bg-muted px-3 py-1.5 text-xs font-bold text-foreground"
                >
                  {item}
                </span>
              ))}
            </div>
          </section>
        </div>
      ) : null}

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <Button
          type="button"
          onClick={onGenerateStrategies}
          className="flex-1 font-bold"
        >
          {hasGeneratedStrategies ? "View Commercialisation Strategies" : "Generate Commercialisation Strategies"}
          <ArrowRight aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onRegenerate}
          className="font-bold"
        >
          Regenerate Investment Thesis
        </Button>
      </div>
    </section>
  )
}

function ValueDriverCard({ driver }) {
  const isPositive = driver.tone !== "warning"

  return (
    <li className="rounded-lg border border-border bg-background p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-bold text-foreground">
            <span
              className={cn(
                "h-2 w-2 shrink-0 rounded-full",
                isPositive ? "bg-primary" : "bg-chart-4",
              )}
              aria-hidden="true"
            />
            <span className="min-w-0">{driver.title}</span>
          </p>
          {driver.description ? (
            <p className="mt-2 text-xs font-medium leading-relaxed text-muted-foreground">
              {driver.description}
            </p>
          ) : null}
        </div>
        <RatingStars rating={driver.rating} isPositive={isPositive} />
      </div>
    </li>
  )
}

function RatingStars({ rating, isPositive }) {
  return (
    <span
      aria-label={`${rating} out of 5`}
      className={cn(
        "flex shrink-0 items-center gap-0.5",
        isPositive ? "text-primary" : "text-chart-4",
      )}
    >
      {[1, 2, 3, 4, 5].map((index) => (
        <Star
          key={index}
          className={cn("h-3 w-3", index > rating && "opacity-30")}
          fill="currentColor"
          aria-hidden="true"
        />
      ))}
    </span>
  )
}

function CommercialisationStrategyView({
  strategies,
  selectedStrategy,
  selectedStrategyData,
  submittedFeedback,
  selectingStrategyId,
  isGeneratingEnhancements,
  hasGeneratedValueEnhancements,
  isOutOfDate,
  onSelect,
  onFeedback,
  onProceed,
}) {
  return (
    <>
      {isOutOfDate ? <OutdatedStrategyBanner /> : null}
      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,7fr)_20rem]">
        <section className="space-y-6">
          {strategies.map((strategy, index) => {
            const selectionId = strategyPersistedId(strategy)

            return (
              <ConceptStrategyCard
                key={selectionId}
                strategy={strategy}
                strategyNumber={index + 1}
                isSelected={selectedStrategy === selectionId}
                isMuted={Boolean(selectedStrategy && selectedStrategy !== selectionId)}
                isSelecting={selectingStrategyId === selectionId}
                isSelectionLocked={Boolean(selectingStrategyId)}
                hasFeedback={Boolean(submittedFeedback[selectionId] ?? submittedFeedback[strategy.id])}
                onSelect={() => onSelect(selectionId)}
                onFeedback={onFeedback}
              />
            )
          })}
        </section>

        <ConceptDecisionPanel
          selectedStrategy={selectedStrategy}
          selectedStrategyData={selectedStrategyData}
          isSelectingStrategy={Boolean(selectingStrategyId) || isGeneratingEnhancements}
          pendingActionLabel={isGeneratingEnhancements ? "Generating Execution Actions..." : undefined}
          actionLabel={hasGeneratedValueEnhancements ? "View Execution Actions" : "Proceed to Execution Actions"}
          actionIcon={Play}
          onRunAppraisal={onProceed}
        />
      </div>
    </>
  )
}

function ValueEnhancementView({
  contract,
  isOutOfDate,
  pendingActionId,
  onAdd,
  onBack,
  onDismiss,
  onRunAppraisal,
}) {
  const selectedStrategy = contract.selectedStrategy

  return (
    <div className="mx-auto grid max-w-5xl items-start gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <section className="rounded-lg border border-border bg-card p-6 text-card-foreground shadow-sm">
        <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Execution Actions
        </h3>
        <div className="mt-6 space-y-4">
          {contract.valueEnhancements.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
              No active execution actions.
            </p>
          ) : contract.valueEnhancements.map((item) => (
            <ConceptExecutionActionCard
              key={item.id}
              action={item}
              disabled={pendingActionId === item.id}
              onAdd={onAdd}
              onDismiss={onDismiss}
            />
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Button
            type="button"
            disabled={!selectedStrategy}
            onClick={onRunAppraisal}
            className="flex-1 font-bold"
          >
            Proceed to Appraisal
            <ArrowRight aria-hidden="true" />
          </Button>
          <Button type="button" variant="secondary" onClick={onBack} className="font-bold">
            Back to Strategies
          </Button>
        </div>
      </section>

      <div className="space-y-4 lg:sticky lg:top-8">
        <aside className="rounded-lg border border-primary/30 bg-primary/5 p-6 text-card-foreground shadow-sm">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-primary">
            Active Strategy
          </h3>
          <p className="mt-2 text-sm font-bold leading-snug text-foreground">
            {selectedStrategy?.name ?? "No strategy selected"}
          </p>
          <dl className="mt-6 space-y-4">
            <div className="flex items-center justify-between border-t border-border pt-4">
              <MetaItem label="Confidence" value={selectedStrategy ? shortConfidence(selectedStrategy.confidence) : "Pending"} />
              <span className="rounded bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
                Active
              </span>
            </div>
            <MetaItem label="Investment Thesis" value={contract.thesis.commercialJudgement} />
            <MetaItem label="Commercialisation" value={selectedStrategy?.strategyType ?? selectedStrategy?.name ?? "Pending"} />
          </dl>
        </aside>
        {isOutOfDate ? <OutdatedDependencyNotice /> : null}
      </div>
    </div>
  )
}

function OutdatedThesisBanner({ actionLabel, onAction, disabled }) {
  return (
    <div className="mb-8 flex flex-col items-start justify-between gap-4 rounded-xl border border-chart-4/30 bg-chart-4/10 p-5 sm:flex-row sm:items-center">
      <div>
        <h4 className="mb-1 flex items-center gap-2 text-sm font-bold text-chart-4">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          New evidence has been added
        </h4>
        <p className="text-xs font-medium text-chart-4">
          Changes have been detected since this Investment Thesis was generated.
          <br />
          Your current investment judgement may no longer reflect the strongest opportunity.
        </p>
      </div>
      <Button
        type="button"
        disabled={disabled}
        onClick={onAction}
        className="shrink-0 rounded-lg bg-chart-4 px-5 font-bold text-primary-foreground hover:bg-chart-4/90"
      >
        {actionLabel}
      </Button>
    </div>
  )
}

function OutdatedStrategyBanner() {
  return (
    <div className="mb-8 rounded-xl border border-chart-4/30 bg-chart-4/10 p-5">
      <h4 className="mb-1 flex items-center gap-2 text-sm font-bold text-chart-4">
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        Generated from an outdated Investment Thesis
      </h4>
      <p className="text-xs font-medium text-chart-4">
        New evidence was added after these strategies were generated.
      </p>
    </div>
  )
}

function OutdatedDependencyNotice() {
  return (
    <div className="rounded-xl border border-chart-4/30 bg-chart-4/10 p-4">
      <h4 className="flex items-center gap-2 text-sm font-bold text-chart-4">
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        Outdated Dependency
      </h4>
      <p className="mt-2 text-xs font-medium text-chart-4">
        Generated from an outdated Investment Thesis.
      </p>
    </div>
  )
}

function MetaItem({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm font-bold text-foreground">{value}</dd>
    </div>
  )
}

function StatusMetaItem({ label, value }) {
  const isOutOfDate = value === "Out of Date"

  return (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1">
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-bold",
            isOutOfDate ? "bg-chart-4/10 text-chart-4" : "bg-primary/10 text-primary",
          )}
        >
          <span
            className={cn("h-2 w-2 rounded-full", isOutOfDate ? "bg-chart-4" : "bg-primary")}
            aria-hidden="true"
          />
          {value}
        </span>
      </dd>
    </div>
  )
}

function shortConfidence(value) {
  return String(value ?? "Pending").replace(/\s*Confidence$/i, "")
}

function conceptStrategyIdFromSelection(selection, strategies = CONCEPT_STRATEGIES) {
  if (!selection) return null

  const rawValue = typeof selection === "string"
    ? selection
    : strategyPersistedId(selection) ?? selection.name ?? selection.title ?? selection.label
  const matchedStrategy = strategies.find((strategy) => (
    strategyMatchesSelection(strategy, rawValue) || strategy.name === rawValue
  ))

  return strategyPersistedId(matchedStrategy) ?? null
}

function formatGeneratedTimestamp(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "Latest run"

  const dateText = date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
  const timeText = date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  })

  return `${dateText} - ${timeText}`
}

function formatEvidenceVersion(value) {
  if (!value) return "Evidence Version Latest"

  return `Evidence Version ${String(value).slice(0, 8)}`
}

function formatShortEvidenceVersion(value) {
  if (!value) return "VLatest"

  return `V${String(value).slice(0, 8)}`
}

function confidenceWidthClass(score) {
  if (score >= 90) return "w-[90%]"
  if (score >= 80) return "w-[80%]"
  if (score >= 70) return "w-[70%]"
  if (score >= 60) return "w-[60%]"
  if (score >= 50) return "w-[50%]"
  if (score >= 40) return "w-[40%]"
  if (score >= 30) return "w-[30%]"
  if (score >= 20) return "w-[20%]"
  if (score >= 10) return "w-[10%]"

  return "w-[0%]"
}
